import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft, ChevronRight, Plus, RefreshCw, Filter as FilterIcon,
  PhoneCall, Building2, FileWarning, Calendar as CalIcon, Search,
} from "lucide-react";
import { cn, dateLocale } from "@/lib/utils";
import { toast } from "sonner";

import WeekView from "@/components/calendar/WeekView";
import MonthView from "@/components/calendar/MonthView";
import DayView from "@/components/calendar/DayView";
import AgendaView from "@/components/calendar/AgendaView";
import EventEditor from "@/components/calendar/EventEditor";
import EventDetailSheet from "@/components/calendar/EventDetailSheet";
import { EVENT_TYPES, FILTER_CATEGORIES, TYPE_ORDER, getMeta } from "@/components/calendar/calendarTypes";
import { useLang, translateFr as tfr } from "@/i18n";
import {
  startOfWeek, addDays, formatWeekLabel, FR_MONTHS,
  gmtOffsetLabel, isSameDay,
} from "@/components/calendar/calendarHelpers";

const VIEWS = ["day", "week", "month", "agenda"];

export default function CalendarPage() {
  const { t } = useLang();
  const today = useMemo(() => new Date(), []);
  // Detect mobile to default to "agenda".
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? "agenda" : "week"));
  // Anchor date: which week/month/day is currently displayed.
  const [anchor, setAnchor] = useState(today);
  const [selectedDate, setSelectedDate] = useState(today);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Editor + detail state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDefaultDate, setEditorDefaultDate] = useState(today);
  const [editorEvent, setEditorEvent] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Date range to fetch — broad month-around-anchor to populate week/month/day/agenda.
  const range = useMemo(() => {
    const a = new Date(anchor);
    const start = new Date(a.getFullYear(), a.getMonth() - 1, 1);
    const end = new Date(a.getFullYear(), a.getMonth() + 2, 0, 23, 59, 59);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/events", { params: range });
      setEvents(r.data || []);
      setLastRefresh(new Date());
      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, [range]);

  const refresh = useCallback(async () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      toast.error("Connexion indisponible.");
      return;
    }
    const ok = await load();
    if (ok) toast.success(tfr("Calendrier à jour"));
    else toast.error(tfr("Impossible d'actualiser le calendrier."), { action: { label: tfr("Réessayer"), onClick: () => refresh() } });
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // -------- Filter pipeline --------
  const filteredEvents = useMemo(() => {
    const cat = FILTER_CATEGORIES.find((f) => f.value === filter);
    let arr = events;
    if (cat && cat.types) arr = arr.filter((e) => cat.types.includes(e.type));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((e) => {
        const inTitle = (e.title || "").toLowerCase().includes(q);
        const inLoc = (e.location || "").toLowerCase().includes(q);
        const inDesc = (e.description || e.notes || "").toLowerCase().includes(q);
        const inPart = (e.participants || "").toLowerCase().includes(q);
        return inTitle || inLoc || inDesc || inPart;
      });
    }
    return arr;
  }, [events, filter, search]);

  // -------- Period navigation --------
  const goPrev = () => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() - 1);
    else if (view === "day") d.setDate(d.getDate() - 1);
    else d.setDate(d.getDate() - 7);
    setAnchor(d);
    if (view === "day") setSelectedDate(d);
  };
  const goNext = () => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + 1);
    else if (view === "day") d.setDate(d.getDate() + 1);
    else d.setDate(d.getDate() + 7);
    setAnchor(d);
    if (view === "day") setSelectedDate(d);
  };
  const goToday = () => {
    const t = new Date();
    setAnchor(t);
    setSelectedDate(t);
  };

  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);

  const periodLabel = useMemo(() => {
    if (view === "month") return `${FR_MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
    if (view === "day") return new Date(anchor).toLocaleDateString(dateLocale(), { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return formatWeekLabel(weekStart);
  }, [view, anchor, weekStart]);

  // -------- Insights --------
  const insights = useMemo(() => {
    const todayKey = today.toDateString();
    const weekEnd = addDays(weekStart, 7);
    const tomorrow = addDays(today, 1);
    const relancesToday = events.filter(
      (e) => ["relance", "follow-up", "call"].includes(e.type)
        && new Date(e.starts_at).toDateString() === todayKey
        && e.status !== "done"
    ).length;
    const rdvClubWeek = events.filter(
      (e) => e.type === "rdv-club"
        && new Date(e.starts_at) >= weekStart
        && new Date(e.starts_at) < weekEnd
    ).length;
    const deadlineTomorrow = events.filter(
      (e) => e.type === "deadline"
        && new Date(e.starts_at).toDateString() === tomorrow.toDateString()
    ).length;
    return { relancesToday, rdvClubWeek, deadlineTomorrow };
  }, [events, today, weekStart]);

  // -------- Editor handlers --------
  const openCreate = (date) => {
    setEditorEvent(null);
    setEditorDefaultDate(date || selectedDate || today);
    setEditorOpen(true);
  };
  const openEdit = (ev) => {
    setEditorEvent(ev);
    setEditorOpen(true);
  };
  const handleSaved = () => {
    toast.success(tfr("Calendrier mis à jour"));
    load();
  };

  // -------- Detail handlers --------
  const openDetail = (ev) => {
    setDetailEvent(ev);
    setDetailOpen(true);
  };
  const handleChanged = (updated) => {
    setEvents((arr) => arr.map((e) => (e.id === updated.id ? updated : e)));
  };
  const handleDeleted = (id) => {
    setEvents((arr) => arr.filter((e) => e.id !== id));
  };

  // Slot click → create at slot time
  const handleSlotClick = (slot) => openCreate(slot);

  // -------- Render --------
  return (
    <div className="px-4 md:px-8 py-5 md:py-8 space-y-5 md:space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* ===== Header ===== */}
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-4xl font-semibold tracking-tight">{t("nav.calendar")}</h1>
          <p className="text-sm text-muted-foreground mt-1 hidden md:block">
            {t("calendar.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CategoryFilter value={filter} onChange={setFilter} open={filterOpen} onOpenChange={setFilterOpen} />
          <Button
            onClick={() => openCreate()}
            className="bg-violet-500 text-white hover:bg-violet-600 h-10 px-4 shadow-lg shadow-violet-500/20"
            data-testid="cal-add-event"
          >
            <Plus className="h-4 w-4 mr-2" /> {tfr("Nouvel événement")}
          </Button>
        </div>
      </header>

      {/* ===== Insights row ===== */}
      <InsightsRow insights={insights} loading={loading} />

      {/* ===== Controls ===== */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={goToday}
            className="h-9 px-3 border-border hover:bg-accent dark:border-border dark:hover:bg-secondary/60"
            data-testid="cal-today"
          >
            {tfr("Aujourd'hui")}
          </Button>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={goPrev}
              className="h-9 w-9 border-border hover:bg-accent dark:border-border dark:hover:bg-secondary/60"
              data-testid="cal-prev"
              aria-label={tfr("Précédent")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goNext}
              className="h-9 w-9 border-border hover:bg-accent dark:border-border dark:hover:bg-secondary/60"
              data-testid="cal-next"
              aria-label={tfr("Suivant")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="ml-1 text-sm md:text-base font-semibold capitalize tabular-nums" data-testid="cal-period-label">
            {periodLabel}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Search (md+) */}
          <div className="relative hidden md:block">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("nav.search")}
              className="h-9 w-56 rounded-md bg-card border border-border dark:bg-surface/60 dark:border-border pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500/40"
              data-testid="cal-search"
            />
          </div>
          <ViewSwitcher value={view} onChange={setView} />
        </div>
      </div>

      {/* ===== Active filter chip ===== */}
      {(filter !== "all" || search) && (
        <div className="flex items-center gap-2 text-xs flex-wrap">
          {filter !== "all" && (
            <Badge variant="outline" className="border-violet-300 text-violet-700 bg-violet-100 dark:border-violet-500/30 dark:text-violet-300 dark:bg-violet-500/10">
              {(FILTER_CATEGORIES.find((f) => f.value === filter) || {}).label}
              <button
                onClick={() => setFilter("all")}
                className="ml-2 opacity-70 hover:opacity-100"
                aria-label={tfr("Effacer le filtre")}
              >×</button>
            </Badge>
          )}
          {search && (
            <Badge variant="outline" className="border-border dark:border-border">
              «{search}»
              <button onClick={() => setSearch("")} className="ml-2 opacity-70 hover:opacity-100">×</button>
            </Badge>
          )}
          <span className="text-muted-foreground tabular-nums">
            {filteredEvents.length} résultat{filteredEvents.length > 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* ===== Views ===== */}
      <div>
        {filteredEvents.length === 0 && events.length > 0 ? (
          <EmptyState
            title={tfr("Aucun événement pour ce filtre.")}
            subtitle={tfr("Essayez une autre catégorie ou élargissez votre recherche.")}
            cta={
              <Button variant="outline" onClick={() => { setFilter("all"); setSearch(""); }} className="border-border dark:border-border">
                {tfr("Réinitialiser les filtres")}
              </Button>
            }
          />
        ) : events.length === 0 ? (
          <EmptyState
            title={tfr("Votre calendrier est vide")}
            subtitle={tfr("Planifiez une relance, un rendez-vous club ou une deadline contrat.")}
            cta={
              <Button onClick={() => openCreate()} className="bg-violet-500 text-white hover:bg-violet-600">
                <Plus className="h-4 w-4 mr-2" /> Créer un événement
              </Button>
            }
          />
        ) : view === "week" && !isMobile ? (
          <WeekView
            weekStart={weekStart}
            events={filteredEvents}
            onEventClick={openDetail}
            onSlotClick={handleSlotClick}
          />
        ) : view === "month" ? (
          <MonthView
            anchor={anchor}
            events={filteredEvents}
            onDayClick={(d) => { setSelectedDate(d); setAnchor(d); setView(isMobile ? "agenda" : "day"); }}
            onEventClick={openDetail}
          />
        ) : view === "day" ? (
          <DayView
            date={anchor}
            events={filteredEvents}
            onEventClick={openDetail}
            onSlotClick={handleSlotClick}
          />
        ) : (
          // Default for mobile + "agenda" desktop choice
          <AgendaView
            weekStart={weekStart}
            events={filteredEvents}
            selectedDate={selectedDate}
            onSelectDate={(d) => { setSelectedDate(d); setAnchor(d); }}
            onEventClick={openDetail}
            onAddEvent={(d) => openCreate(d)}
          />
        )}
      </div>

      {/* ===== Legend + Sync ===== */}
      <footer className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 pt-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-muted-foreground">
          {TYPE_ORDER.slice(0, 7).map((k) => {
            const m = EVENT_TYPES[k];
            return (
              <span key={k} className="flex items-center gap-1.5">
                <span className={cn("h-1.5 w-1.5 rounded-full", m.legendDot)} />
                {m.legend}
              </span>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:inline">{gmtOffsetLabel()}</span>
          {lastRefresh && (
            <span className="text-[10px] text-muted-foreground hidden sm:inline tabular-nums" data-testid="cal-last-refresh">
              {tfr("Mis à jour à")} {lastRefresh.toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
            aria-busy={loading}
            aria-label={tfr("Actualiser le calendrier")}
            className="h-9"
            data-testid="cal-sync"
          >
            <RefreshCw className={cn("h-3.5 w-3.5 mr-2", loading && "animate-spin motion-reduce:animate-none")} aria-hidden />
            {loading ? tfr("Actualisation…") : tfr("Actualiser")}
          </Button>
        </div>
      </footer>

      {/* ===== Modals ===== */}
      <EventEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        defaultDate={editorDefaultDate}
        event={editorEvent}
        onSaved={handleSaved}
      />
      <EventDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        event={detailEvent}
        onEdit={openEdit}
        onChanged={handleChanged}
        onDeleted={handleDeleted}
      />
    </div>
  );
}

// ============== Sub-components ==============

function ViewSwitcher({ value, onChange }) {
  const items = [
    { v: "day", label: tfr("Jour") },
    { v: "week", label: tfr("Semaine") },
    { v: "month", label: tfr("Mois") },
    { v: "agenda", label: "Agenda" },
  ];
  return (
    <div
      className="inline-flex items-center rounded-lg border border-border bg-card dark:border-border dark:bg-surface/60 p-0.5"
      data-testid="cal-view-switcher"
    >
      {items.map((it) => (
        <button
          key={it.v}
          onClick={() => onChange(it.v)}
          className={cn(
            "px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-150",
            value === it.v
              ? "bg-violet-100 text-violet-700 ring-1 ring-violet-300 dark:bg-violet-500/15 dark:text-violet-300 dark:ring-violet-500/30"
              : "text-muted-foreground hover:text-foreground"
          )}
          data-testid={`cal-view-${it.v}`}
          aria-pressed={value === it.v}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function CategoryFilter({ value, onChange, open, onOpenChange }) {
  const current = FILTER_CATEGORIES.find((f) => f.value === value) || FILTER_CATEGORIES[0];
  return (
    <div className="relative">
      <Button
        variant="outline"
        onClick={() => onOpenChange(!open)}
        className="h-10 px-3 border-border hover:bg-accent dark:border-border dark:hover:bg-secondary/60"
        data-testid="cal-filter-trigger"
      >
        <FilterIcon className="h-4 w-4 mr-2 text-muted-foreground" />
        <span className="text-sm">{current.label}</span>
        <ChevronRight className={cn("h-3 w-3 ml-2 transition-transform", open && "rotate-90")} />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => onOpenChange(false)} />
          <div
            className="absolute right-0 mt-2 z-40 w-60 rounded-xl border border-border bg-popover dark:border-border dark:bg-surface shadow-xl py-1 max-h-[60vh] overflow-y-auto"
            data-testid="cal-filter-menu"
          >
            {FILTER_CATEGORIES.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { onChange(opt.value); onOpenChange(false); }}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-accent dark:hover:bg-secondary/60 transition-colors",
                  value === opt.value && "text-violet-700 dark:text-violet-300"
                )}
                data-testid={`cal-filter-opt-${opt.value}`}
              >
                <span>{opt.label}</span>
                {value === opt.value && <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InsightsRow({ insights, loading }) {
  const items = [
    {
      key: "relances",
      icon: PhoneCall,
      label: insights.relancesToday > 1 ? tfr("relances aujourd'hui") : tfr("relance aujourd'hui"),
      value: insights.relancesToday,
      tone: insights.relancesToday > 0 ? "text-rose-700 border-rose-300 bg-rose-50 dark:text-rose-300 dark:border-rose-500/30 dark:bg-rose-500/5" : "text-muted-foreground border-border bg-card dark:border-border dark:bg-surface/40",
    },
    {
      key: "rdv-club",
      icon: Building2,
      label: tfr("rendez-vous club cette semaine"),
      value: insights.rdvClubWeek,
      tone: insights.rdvClubWeek > 0 ? "text-sky-700 border-sky-300 bg-sky-50 dark:text-sky-300 dark:border-sky-500/30 dark:bg-sky-500/5" : "text-muted-foreground border-border bg-card dark:border-border dark:bg-surface/40",
    },
    {
      key: "deadline",
      icon: FileWarning,
      label: insights.deadlineTomorrow > 1 ? tfr("deadlines contrat demain") : tfr("deadline contrat demain"),
      value: insights.deadlineTomorrow,
      tone: insights.deadlineTomorrow > 0 ? "text-red-700 border-red-300 bg-red-50 dark:text-red-300 dark:border-red-500/30 dark:bg-red-500/5" : "text-muted-foreground border-border bg-card dark:border-border dark:bg-surface/40",
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5" data-testid="cal-insights">
      {items.map((it) => (
        <div
          key={it.key}
          className={cn(
            "rounded-xl border px-3.5 py-2.5 flex items-center gap-3 transition-colors duration-150",
            it.tone
          )}
        >
          <it.icon className="h-4 w-4 shrink-0 opacity-80" />
          <div className="min-w-0">
            <span className="tabular-nums font-semibold text-foreground/95 mr-1.5">{loading ? "—" : it.value}</span>
            <span className="text-[12px]">{it.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, subtitle, cta }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/60 dark:border-border dark:bg-surface/30 py-14 px-6 flex flex-col items-center justify-center text-center" data-testid="cal-empty">
      <div className="h-12 w-12 rounded-full bg-secondary dark:bg-secondary/50 flex items-center justify-center mb-3">
        <CalIcon className="h-5 w-5 text-muted-foreground" />
      </div>
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>
      <div className="mt-4">{cta}</div>
    </div>
  );
}
